<template>
  <Page>
    <div
      class="container container--no-padding"
      :class="$style['store']">
      <div :class="$style['store__image-wrapper']">
        <img
          :class="$style['store__image']"
          format="webp"
          sizes="100vw"
          loading="lazy"
          :srcset="
            `${config.public.cdnURL}pictures/shops/medium/${storeData.name}.webp` +
              ' 800w,' +
              `${config.public.cdnURL}pictures/shops/full/${storeData.name}.webp` +
              ' 1920w, ' +
              `${config.public.cdnURL}pictures/shops/hd/${storeData.name}.webp` +
              ' 3000w'
          ">
      </div>
      <div class="container container--small page--static">
        <Btn
          size="small"
          icon-left="System/arrow-left-s-line"
          class="h-margin-bottom back-button"
          @click="hasHistory ? $router.go(-1) : $router.push({ name: $lang + '-stores' })">
          {{ $t('general/go-back') }}
        </Btn>
        <h1>{{ storeData.title }}</h1>
        <b>
          {{ storeData.address.street }}
          {{ storeData.address.housenr }},
          {{ storeData.address.postalcode }}
        </b>
        <Grid class="h-margin-top">
          <Cell
            tablet="12"
            size="12"
            :class="$style['store__mobile-opening-hours']">
            <p>
              {{ storeData.description }}
            </p>
            <StoreStatus class="h-hide-mobile-up" :store-name="storeData.name" :opening-hours="storeData.openingHours" :special-opens-at="storeData.specialOpensAt" />
          </Cell>
          <Cell
            tablet="6"
            size="12">
            <StoreHours :store="storeData" />
            <div :class="$style['store__contact-info']">
              <h3>{{ $t('general/contact-information') }}</h3>
              <Btn
                full-width="all"
                icon-left="Device/phone-line"
                class="h-margin-top"
                :href="'tel:' + storeData.phone">
                {{ storeData.phone }}
              </Btn>
              <Btn
                href="mailto:info@gutsgusto.com"
                full-width="all"
                icon-left="Business/mail-line"
                class="h-margin-top-s">
                <div v-if="$lang == 'de'">
                  service@gutsgusto.de
                </div>
                <div v-else>
                  info@gutsgusto.com
                </div>
              </Btn>
            </div>
          </Cell>
          <Cell
            tablet="6"
            size="12">
            <h3 class="h-margin-bottom-s">
              {{ $t('general/location') }}
            </h3>
            <StoreMap
              :initial-zoom="14"
              :store-detail="true"
              :store="storeData.name"
              :initial-center="[
                storeData.geolocation.lat,
                storeData.geolocation.long
              ]"
              :class="$style['store__map']"
              :stores="stores" />
          </Cell>
          <Cell
            tablet="6"
            size="12" />
        </Grid>
        <ServiceBanner class="h-margin-top-xl" />
      </div>
    </div>
  </Page>
</template>
<script setup>
import {
  formatCanonical,
  formatOpenGraph,
  formatMetaDescription
} from '~/helpers/metadata'
const { $api } = useNuxtApp()
const config = useRuntimeConfig()
const route = useRoute()

const { data } = await useAsyncData(`shops-${route.params.store}`, async () => {
  const [stores, navigation] = await Promise.all([
    $api('shops'),
    $api(`navigation/${route.params.store}`)
  ])

  return { stores, navigation }
})

const navigation = computed(() => {
  return data.value.navigation
})

const stores = computed(() => {
  return data.value.stores
})

const storeData = computed(() => {
  return stores.value.find(element => element.slug === route.params.store)
})

const hasHistory = computed(() => {
  return process.client && window.history.length > 1
})

useHead({
  title: navigation.value.metaData.pageTitle,
  titleTemplate: '%s',
  link: [...formatCanonical(navigation.value.metaData)],
  meta: [
    ...formatOpenGraph(navigation.value.metaData.openGraph),
    formatMetaDescription(navigation.value.metaData)
  ],
  script: [{ type: 'application/ld+json', innerHTML: navigation.value.metaData.schemaOrg }] // Structured data
})
await useI18n([
  'stores',
  'openinghours'
])
</script>
  <style lang="scss" module>
  .store {
    &__image-wrapper {
      background-color: $grey-light;
      display: flex;
      width: 100%;
      height: 60vw;
      max-height: 60vh;
    }

    &__image {
      object-fit: cover;
      width: 100%;
    }

    &__mobile-opening-hours {
      align-items: flex-start;
    }

    &__contact-info {
      margin-top: $spacing * 2;
    }

    &__map {
      z-index: 1;
    }
  }
  </style>
